
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Container } from "inversify";
import FilterContainer from "../organization/FilterContainer.vue";
import { handleError } from "../../../../common/src/utility/errorUtils";
import LoadingOverlay from "../../../../common/src/components/LoadingOverlay.vue";
import { ProductTypeStore } from "../../../../common/src/stores/ProductTypeStore";
import { ServiceTypeStore } from "../../../../common/src/stores/ServiceTypeStore";
import { OperatingRegimeTypeStore } from "../../../../common/src/stores/OperatingRegimeTypeStore";
import { ApplicationTypeStore } from "../../../../common/src/stores/ApplicationTypeStore";
import { ReportListItem } from "@gsx/common/src/types/http/admin/report";
import { AskDownloadReport } from "../../../../common/src/utility/confirmations";
import { NotificationStore } from "../../../../common/src/stores/NotificationStore";
import { Api } from "../../../../common/src/api/Api";
import { AuthStore } from "../../../../common/src/stores/AuthStore";
import {
    hasQuiltyBasicSubscription,
    hasQuiltyEnterpriseSubscription,
    hasQuiltyIntelligenceSubscription,
} from "@gsx/common";
import { PaymentService } from "../../../../landing/src/utility/payment/PaymentService";
import { ReportFilterStore } from "../../stores/reports/ReportFilterStore";
import axios from "axios";
import axiosRetry from "axios-retry";

const axiosInstance = axios.create();

axiosRetry(axiosInstance, {
    retries: 3,
    retryCondition: (error) =>
        axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status === 500,
    retryDelay: axiosRetry.exponentialDelay,
});

@Component({
    components: {
        FilterContainer,
        LoadingOverlay,
    },
})
export default class ReportListItemView extends Vue {
    @Inject("container") readonly container!: Container;
    readonly reportFilterStore: ReportFilterStore = this.container.get(ReportFilterStore);
    readonly productTypeStore: ProductTypeStore = this.container.get(ProductTypeStore);
    readonly serviceTypeStore: ServiceTypeStore = this.container.get(ServiceTypeStore);
    readonly operatingRegimeTypeStore: OperatingRegimeTypeStore =
        this.container.get(OperatingRegimeTypeStore);
    readonly applicationTypeStore: ApplicationTypeStore = this.container.get(ApplicationTypeStore);
    readonly notificationStore: NotificationStore = this.container.get(NotificationStore);
    readonly api: Api = this.container.get(Api);
    readonly authStore: AuthStore = this.container.get(AuthStore);
    readonly paymentService: PaymentService = this.container.get(PaymentService);

    @Prop() report!: ReportListItem;
    // @Prop({ default: false, type: Boolean }) isDashboardView?: boolean;

    chosenPDFBlob?: any = null;
    loadingPDFBlob = false;
    failedLoadingPDFBlob = false;

    recentlyDownloaded: { [reportId: string]: string } = {};

    fullReportView: boolean = false;

    modalShowTags: boolean = false;
    tableShowTags: boolean = false;

    mounted() {
        this.productTypeStore.load().catch(handleError);
        this.serviceTypeStore.load().catch(handleError);
        this.operatingRegimeTypeStore.load().catch(handleError);
        this.applicationTypeStore.load().catch(handleError);
    }

    get downloadedReports() {
        if (this.authStore.user && this.authStore.user.downloadedReports) {
            return this.authStore.user.downloadedReports;
        }

        return [];
    }

    get isReportDownloaded() {
        return (
            this.downloadedReports.length &&
            this.downloadedReports.find(
                (downloadedReport) => downloadedReport.reportId === this.report.reportId,
            )
        );
    }

    get sasToken() {
        return this.authStore.sasToken;
    }

    organizationUrl(organizationId: string) {
        return `/dashboard/organizations/${organizationId}`;
    }

    get discountAvailable() {
        return hasQuiltyBasicSubscription(this.authStore.user);
    }

    get hasPDFAccess() {
        return (
            this.recentlyDownloaded[this.report.reportId] ||
            this.report.isFree ||
            this.isReportDownloaded ||
            hasQuiltyEnterpriseSubscription(this.authStore.user)
        );
    }

    get hasPDFCredits() {
        if (hasQuiltyIntelligenceSubscription(this.authStore.user)) {
            // set maximum downloads per quarter
            const maxReportsToDownloadInAQuarter = 2;

            // Determine the current quarter
            const currentQuarter = Math.floor(new Date().getMonth() / 3);

            // Set the start date of the current quarter
            const startOfQuarter = new Date();
            startOfQuarter.setMonth(currentQuarter * 3);
            startOfQuarter.setDate(1);
            startOfQuarter.setHours(0, 0, 0, 0);

            return (
                !this.downloadedReports.length ||
                this.downloadedReports.filter(
                    (downloadedReport) =>
                        downloadedReport.date.getTime() >= startOfQuarter.getTime(),
                ).length < maxReportsToDownloadInAQuarter
            );
        } else {
            return false;
        }
    }

    async downloadPDF() {
        const confirmDownload = await AskDownloadReport(this.report.title);
        if (confirmDownload.value) {
            await this.initializePDFViewer();
        }
    }

    async purchasePDF() {
        try {
            await this.paymentService.purchaseReport(this.report.reportId);
            this.authStore.me();
        } catch (e) {
            this.notificationStore.warning("Could not initialize a payment process.");
        }
    }

    async reportView() {
        //@ts-ignore
        $(`#report-modal-${this.report.reportId}`).modal();
        if (this.hasPDFAccess) {
            await this.initializePDFViewer();
        } else {
            this.chosenPDFBlob = null;
        }
    }

    async initializePDFViewer() {
        this.loadingPDFBlob = true;
        const pdfBlob = await this.fetchPDFBlob();
        if (pdfBlob) {
            this.chosenPDFBlob = URL.createObjectURL(pdfBlob);
        } else {
            console.error("ERROR INITIALIZING PDF VIEWER");
        }
        this.loadingPDFBlob = false;
    }

    async fetchPDFBlob() {
        try {
            const pdf = await this.fetchPDFUrl();
            if (pdf) {
                try {
                    const response = await axiosInstance.get(pdf, {
                        responseType: "blob",
                    });
                    return response.data;
                } catch (err) {
                    this.failedLoadingPDFBlob = true;
                    console.error("ERROR FETCHING PDF", err);
                    this.notificationStore.danger("Error downloading PDF viewer.");
                }
            }
        } catch (err: any) {
            if (err.response.status >= 500) {
                this.failedLoadingPDFBlob = true;
            }
        }
    }

    async fetchPDFUrl() {
        let downloadedPDF = null;
        if (this.recentlyDownloaded[this.report.reportId]) {
            downloadedPDF = this.recentlyDownloaded[this.report.reportId];
        } else {
            downloadedPDF = await this.api.dashboard.reports.downloadReport(this.report.reportId);
            this.recentlyDownloaded[this.report.reportId] = downloadedPDF;
            this.authStore.me();
        }

        let chosenPDF = `${downloadedPDF}?${this.sasToken}`;

        return chosenPDF;
    }

    async autoDownload() {
        const pdfBlob = await this.fetchPDFBlob();
        if (pdfBlob) {
            // Create a temporary anchor element and trigger a download
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(pdfBlob);
            link.download = this.report.title.replace(/[^a-z0-9]/gi, "_").toLowerCase() + ".pdf"; // Replace any non-alphanumeric character with an underscore
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    getProductTags(): string[] {
        return (this.report.products || []).map(
            (tag) => this.productTypeStore.idLabelMapping.get(tag) || tag,
        );
    }

    getServiceTags(): string[] {
        return (this.report.services || []).map(
            (tag) => this.serviceTypeStore.idLabelMapping.get(tag) || tag,
        );
    }

    getOperatingRegimeTags(): string[] {
        return (this.report.operatingRegimeTags || []).map(
            (tag) => this.operatingRegimeTypeStore.idLabelMapping.get(tag) || tag,
        );
    }

    getApplicationTags(): string[] {
        return (this.report.applicationTags || []).map(
            (tag) => this.applicationTypeStore.idLabelMapping.get(tag) || tag,
        );
    }

    getOrgTags(): { label: string; link?: string | undefined }[] {
        const orgTags = (this.report.organizations || []).map((org) => ({
            label: org.organizationName,
            link: this.organizationUrl(org.organizationId),
        }));
        return [...orgTags];
    }

    getAllTags(): { label: string; link?: string | undefined }[] {
        const productTags = this.getProductTags().map((tag) => ({ label: tag }));
        const serviceTags = this.getServiceTags().map((tag) => ({ label: tag }));
        const operatingRegimeTags = this.getOperatingRegimeTags().map((tag) => ({ label: tag }));
        const applicationTags = this.getApplicationTags().map((tag) => ({ label: tag }));

        const orgTags = (this.report.organizations || []).map((org) => ({
            label: org.organizationName,
            link: this.organizationUrl(org.organizationId),
        }));
        const customTags = (this.report.tags || []).map((tag) => ({ label: tag }));
        return [
            ...orgTags,
            ...productTags,
            ...serviceTags,
            ...customTags,
            ...operatingRegimeTags,
            ...applicationTags,
        ];
    }

    toggleModalShowTags() {
        this.modalShowTags = !this.modalShowTags;
    }

    toggleTableShowTags() {
        this.tableShowTags = !this.tableShowTags;
    }
}
